'use client';

import { type MouseEvent, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';

import { useTranslation } from '@cms/i18n/client';
import { Link, type LinkProps } from '@common/atoms/Link';

import styles from './Button.module.scss';

export type ButtonType = 'button' | 'submit' | 'reset';

export type ButtonVariant =
    | 'filled-primary'
    | 'filled-success'
    | 'filled-error'
    | 'filled-inverted'
    | 'filled-white'
    | 'premium'
    | 'outlined-primary'
    | 'outlined-white'
    | 'outlined-inverted'
    | 'text-primary'
    | 'text-default-low'
    | 'text-inverted'
    | 'text-white'
    | 'link'
    | 'national-theme';

export interface Props {
    children?: ReactNode;
    className?: string;
    disabled?: boolean;
    hardRefresh?: boolean;
    href?: string;
    id?: string;
    loading?: boolean;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    ref?: Ref<HTMLButtonElement>;
    tabIndex?: number;
    target?: LinkProps['target'];
    type?: ButtonType;
    variant: ButtonVariant;
}

export const Button = ({
    children,
    className,
    disabled,
    hardRefresh,
    href,
    loading,
    ref,
    type = 'button',
    variant,
    ...rest
}: Props) => {
    const __translate = useTranslation('common').t;

    const buttonClassName = clsx(
        styles['button'],
        styles[`button--variant-${variant}`],
        loading && styles['button--loading'],
        disabled && styles['button--disabled'],
        className,
    );

    const content = loading ? __translate`loading` : children;

    if (href) {
        return (
            <Link className={buttonClassName} href={href} hardRefresh={hardRefresh} {...rest}>
                {content}
            </Link>
        );
    }

    return (
        <button className={buttonClassName} disabled={disabled || loading} ref={ref} type={type} {...rest}>
            {content}
        </button>
    );
};
